<template>
  <!-- Panel de captura flotante (estilo del sidebar). Muestra la imagen generada,
       permite descargarla (PNG/JPG/WebP) y guardarla. El botón de guardar conserva
       data-action="save-screenshot" (contrato del bot de capturas) — NO tocar. -->
  <div class="epc-screenshot-panel" data-panel="screenshot">
    <div class="epc-screenshot-header">
      <span class="epc-screenshot-title">
        <b-icon pack="fas" icon="camera" size="is-small"></b-icon>
        {{ $t('screenshot') }}
      </span>
      <button class="epc-screenshot-close" :aria-label="$t('close')" @click="$emit('epc-close', true)">
        <b-icon pack="fas" icon="xmark" size="is-small"></b-icon>
      </button>
    </div>

    <div class="epc-screenshot-divider"></div>

    <div class="epc-screenshot-body">
      <div class="epc-screenshot-label">
        <b-icon icon="images" pack="fas" size="is-small"></b-icon>
        <span>{{ $t('design_image') }}</span>
      </div>

      <div class="epc-screenshot-preview">
        <img :src="screenshot" alt="" />
      </div>

      <div class="epc-screenshot-actions">
        <b-dropdown aria-role="list" position="is-top-right" :mobile-modal="false">
          <template #trigger>
            <b-button size="is-small" icon-left="download" icon-pack="fas">
              {{ $t('download') }}
              <b-icon icon="chevron-down" pack="fas" size="is-small"></b-icon>
            </b-button>
          </template>
          <b-dropdown-item aria-role="listitem" @click="_download('png')">
            <b-icon icon="image" pack="fas" size="is-small"></b-icon> PNG
          </b-dropdown-item>
          <b-dropdown-item aria-role="listitem" @click="_download('jpg')">
            <b-icon icon="file-image" pack="fas" size="is-small"></b-icon> JPG
          </b-dropdown-item>
          <b-dropdown-item aria-role="listitem" @click="_download('webp')">
            <b-icon icon="star" pack="fas" size="is-small"></b-icon> WebP
          </b-dropdown-item>
        </b-dropdown>

        <b-button v-if="canSave" size="is-small" type="is-primary" icon-left="save" icon-pack="fas"
          data-action="save-screenshot"
          @click="$emit('epc-save-screenshot')">
          {{ $t('save') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ViewerScreenshotPanel",

  props: {
    screenshot: { type: String, default: null },
    workLayer: { type: String, default: null },
    canSave: { type: Boolean, default: true }, // false = oculta "guardar en el parque" (contexto usuario)
  },

  methods: {
    async _download(format) {
      const fileName = (this.workLayer || 'screenshot') + '_' + Date.now()

      if (format === 'png') {
        this._triggerDownload(this.screenshot, fileName + '.png')
      } else {
        const mimeTypes = { jpg: 'image/jpeg', webp: 'image/webp' }
        const blobUrl = await this._convertToFormat(this.screenshot, mimeTypes[format], 0.90)
        this._triggerDownload(blobUrl, fileName + '.' + format)
        URL.revokeObjectURL(blobUrl)
      }

      // Tras descargar, cerrar el panel y limpiar el preview.
      this.$emit('epc-close', true)
    },

    _convertToFormat(pngDataUrl, mimeType, quality) {
      return new Promise((resolve) => {
        const image = new Image()
        image.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = image.width
          canvas.height = image.height
          const ctx = canvas.getContext('2d')
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(image, 0, 0)
          canvas.toBlob((blob) => resolve(URL.createObjectURL(blob)), mimeType, quality)
        }
        image.src = pngDataUrl
      })
    },

    _triggerDownload(href, filename) {
      const link = document.createElement('a')
      link.href = href
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.epc-screenshot-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 340px;
  max-width: 85vw;
  background-color: #ffffff;
  border-left: 1px solid #e3e3e8;
  box-shadow: -2px 0 8px 0 rgba(0, 0, 0, 0.08);
  z-index: 7;
  display: flex;
  flex-direction: column;
}

.epc-screenshot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
}

.epc-screenshot-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 14px;
  color: #343a40;
}

.epc-screenshot-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: #495057;
  cursor: pointer;
}

.epc-screenshot-close:hover {
  background-color: #f4f4f6;
}

.epc-screenshot-divider {
  height: 1px;
  margin: 0 12px;
  background-color: #ececf1;
}

.epc-screenshot-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.epc-screenshot-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #868e96;
}

.epc-screenshot-preview {
  border: 1px solid #e3e3e8;
  border-radius: 8px;
  padding: 8px;
  text-align: center;
  background-color: #fafafa;
}

.epc-screenshot-preview img {
  max-width: 100%;
  max-height: 220px;
  border-radius: 4px;
}

.epc-screenshot-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
